import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CellarEvents, Pos, type PosApiKey } from '@/lib/api';
import { apiBase } from '@/lib/api/client';
import { Badge, Button, Card, EmptyState, IconButton, Input, Modal, Skeleton, TableHead } from '@/design-system';
import { formatDate, formatPriceNok } from '@/lib/format';
import { useToasts } from '@/lib/store';
import { UpgradeGate, isEntitlementError } from '@/components/UpgradeGate';

const EXAMPLE_BODY = `{
  "sales": [
    { "externalId": "kvittering-10421-linje-1", "bottleId": "<bottle-id>", "quantity": 1, "unitPriceNok": 349 },
    { "externalId": "kvittering-10421-linje-2", "name": "Barolo 2018", "quantity": 2 }
  ]
}`;

/**
 * POS sales-feed integration page ("POS integrasjon") — a Pro capability.
 * API-key management (create shows the key once), short integration docs,
 * and a recent-sales table sourced from kjellerhistorikk (activity=sold),
 * which both this page's manual "Selg" flow and an external POS feed into.
 */
export function PosPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [createdKey, setCreatedKey] = useState<{ apiKey: PosApiKey; plaintextKey: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const keysQ = useQuery({ queryKey: ['pos.keys'], queryFn: () => Pos.listKeys() });
  const salesQ = useQuery({
    queryKey: ['cellar-events', 'sold', 'pos-page'],
    queryFn: () => CellarEvents.list({ activity: 'sold', limit: 50 }),
  });

  const createKey = useMutation({
    mutationFn: () => Pos.createKey(newName.trim()),
    onSuccess: (result) => {
      setCreatedKey(result);
      setNewName('');
      setCreateOpen(false);
      void qc.invalidateQueries({ queryKey: ['pos.keys'] });
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  const revokeKey = useMutation({
    mutationFn: (id: string) => Pos.revokeKey(id),
    onSuccess: () => {
      push({ title: t('pos.keys.revoked'), tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['pos.keys'] });
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  if (isEntitlementError(keysQ.error)) {
    return <UpgradeGate error={keysQ.error} featureLabel={t('pos.title')} />;
  }

  const keys = keysQ.data ?? [];
  const sales = salesQ.data?.events ?? [];
  const endpoint = `${apiBase || window.location.origin}/api/v1/pos/sales`;

  return (
    <div className="grid gap-6">
      <header>
        <h1 className="ui-section-title text-3xl">{t('pos.title')}</h1>
        <p className="text-sm text-mist">{t('pos.subtitle')}</p>
      </header>

      <Card className="grid gap-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="ui-card-title">{t('pos.keys.title')}</h2>
          <Button iconLeft="plus" onClick={() => setCreateOpen(true)}>
            {t('pos.keys.new')}
          </Button>
        </div>

        {keysQ.isLoading ? (
          <Skeleton height={80} rounded="md" />
        ) : keys.length === 0 ? (
          <EmptyState icon="lock" title={t('pos.keys.empty')} />
        ) : (
          <div className="grid gap-2">
            {keys.map((key) => (
              <div key={key.id} className="ui-card flex flex-wrap items-center justify-between gap-3 p-3">
                <div className="grid gap-0.5">
                  <span className="font-serif text-sm text-parchment">{key.name}</span>
                  <span className="font-mono text-xs text-mist">{key.prefix}</span>
                  <span className="text-[10px] uppercase tracking-widest text-mist">
                    {key.lastUsedAt ? t('pos.keys.lastUsed', { date: formatDate(key.lastUsedAt) }) : t('pos.keys.neverUsed')}
                    {' · '}
                    {t('pos.keys.createdAt', { date: formatDate(key.createdAt) })}
                  </span>
                </div>
                {key.revokedAt ? (
                  <Badge tone="mist">{t('pos.keys.revoked')}</Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft="trash"
                    loading={revokeKey.isPending}
                    onClick={() => {
                      if (window.confirm(t('pos.keys.revokeConfirm'))) revokeKey.mutate(key.id);
                    }}
                  >
                    {t('pos.keys.revoke')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="grid gap-3 p-5">
        <h2 className="ui-card-title">{t('pos.docs.title')}</h2>
        <p className="text-sm text-mist">{t('pos.docs.description')}</p>
        <div className="grid gap-1">
          <span className="text-[10px] uppercase tracking-widest text-mist">{t('pos.docs.endpoint')}</span>
          <code className="ui-card block overflow-x-auto p-2 font-mono text-xs text-parchment">POST {endpoint}</code>
        </div>
        <div className="grid gap-1">
          <span className="text-[10px] uppercase tracking-widest text-mist">{t('pos.docs.header')}</span>
          <code className="ui-card block overflow-x-auto p-2 font-mono text-xs text-parchment">X-Pos-Api-Key: pos_live_…</code>
        </div>
        <div className="grid gap-1">
          <span className="text-[10px] uppercase tracking-widest text-mist">{t('pos.docs.example')}</span>
          <pre className="ui-card overflow-x-auto p-2 font-mono text-xs text-parchment">
            <code>{`curl -X POST ${endpoint} \\\n  -H "X-Pos-Api-Key: pos_live_..." \\\n  -H "Content-Type: application/json" \\\n  -d '${EXAMPLE_BODY.replace(/\n\s*/g, ' ')}'`}</code>
          </pre>
        </div>
        <p className="text-xs text-mist">{t('pos.docs.fields')}</p>
      </Card>

      <Card className="grid gap-4 p-5">
        <h2 className="ui-card-title">{t('pos.recentSales.title')}</h2>
        {salesQ.isLoading ? (
          <Skeleton height={200} rounded="md" />
        ) : sales.length === 0 ? (
          <EmptyState icon="clock" title={t('pos.recentSales.empty')} />
        ) : (
          <div className="overflow-x-auto">
            <div style={{ minWidth: 600 }}>
              <TableHead
                gridTemplateColumns="110px minmax(160px,2fr) 80px 100px"
                columns={[t('history.columns.date'), t('history.columns.wine'), t('history.columns.bottles'), t('history.columns.sell')]}
              />
              <div className="grid gap-1 py-1">
                {sales.map((sale) => (
                  <div key={sale.id} style={{ gridTemplateColumns: '110px minmax(160px,2fr) 80px 100px' }} className="ui-sortable-row items-center">
                    <span className="text-sm text-mist">{formatDate(sale.createdAt)}</span>
                    <span className="truncate font-serif text-sm text-parchment">{sale.wineName}</span>
                    <span className="text-sm text-danger">{sale.quantityDelta}</span>
                    <span className="text-sm text-mist">{formatPriceNok(sale.sellingPriceNok)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={t('pos.keys.new')} footer={
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            iconLeft="check"
            loading={createKey.isPending}
            disabled={!newName.trim()}
            onClick={() => createKey.mutate()}
          >
            {t('pos.keys.new')}
          </Button>
        </div>
      }>
        <Input
          label={t('pos.keys.title')}
          placeholder={t('pos.keys.namePlaceholder')}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          autoFocus
        />
      </Modal>

      <Modal
        open={Boolean(createdKey)}
        onClose={() => {
          setCreatedKey(null);
          setCopied(false);
        }}
        title={t('pos.keys.created')}
        footer={
          <Button
            variant="primary"
            onClick={() => {
              setCreatedKey(null);
              setCopied(false);
            }}
          >
            {t('common.close')}
          </Button>
        }
      >
        <div className="grid gap-3">
          <p className="text-sm text-mist">{t('pos.keys.createdHint')}</p>
          <div className="flex items-center gap-2">
            <code className="ui-card flex-1 overflow-x-auto p-2 font-mono text-xs text-parchment">
              {createdKey?.plaintextKey}
            </code>
            <IconButton
              icon={copied ? 'check' : 'copy'}
              label={t('pos.keys.copy')}
              onClick={async () => {
                if (createdKey) {
                  await navigator.clipboard.writeText(createdKey.plaintextKey);
                  setCopied(true);
                }
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
